import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/apiFetch';
import AppNavActions from '../components/AppNavActions';
import NotFoundMessage from '../components/NotFoundMessage';
import {
  Layers,
  Loader2,
  ArrowLeft,
  ExternalLink,
  FileText,
  Settings,
} from 'lucide-react';
import { getFileIcon } from '../utils/fileUtils';
import { formatGroupLabel } from '../utils/groupLabel';

// --- Interfaces ---

interface GroupRecord {
  id: string;
  title?: string | null;
  content_type?: string | null;
}

interface GroupIngestionProcess {
  id: string;
  title?: string | null;
  status?: string | null;
}

interface Group {
  id: string;
  type?: string | null;
  title?: string | null;
  records: GroupRecord[];
  ingestion_processes: GroupIngestionProcess[];
}

// --- Constants ---

const BRAND_BG = 'bg-emerald-800';
const BRAND_TEXT = 'text-emerald-800';

// --- Main Component ---

export default function GroupDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState<Group | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchGroup = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch(`/api/v1/groups/${id}`);
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Failed to load group (${response.status})`);
        }
        const data = await response.json();
        setGroup(data._embedded?.group || null);
      } catch (err: any) {
        setError(err.message || 'Failed to load group');
      } finally {
        setLoading(false);
      }
    };

    fetchGroup();
  }, [id]);

  // --- Loading State ---
  if (loading) {
    return (
      <div className="flex-1 bg-white text-gray-900 font-sans">
        <Navbar navigate={navigate} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-gray-500 text-sm">Loading group details...</p>
          </div>
        </main>
      </div>
    );
  }

  // --- Error State ---
  if (error || !group) {
    return (
      <div className="flex-1 bg-white text-gray-900 font-sans">
        <Navbar navigate={navigate} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <NotFoundMessage
            message={error || 'The requested group could not be loaded.'}
            onGoBack={() => navigate(-1)}
          />
        </main>
      </div>
    );
  }

  const ingestionProcesses = group.ingestion_processes || [];
  const records = group.records || [];

  return (
    <div className="flex-1 bg-white text-gray-900 font-sans selection:bg-emerald-100 selection:text-emerald-900">
      <Navbar navigate={navigate} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-500 pb-20">

          {/* Back navigation */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-sm text-gray-500 hover:text-emerald-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </button>

          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-visible relative">

            {/* Dark Header */}
            <div className="bg-gray-900 text-white p-6 flex items-start space-x-4 rounded-t-3xl">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shrink-0">
                <Layers className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold tracking-tight break-words pr-4">{formatGroupLabel(group)}</h1>
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-8">

              {ingestionProcesses.length > 0 && (
                <section>
                  <h2 className="text-sm font-bold text-gray-900 flex items-center mb-3">
                    <Settings className="w-4 h-4 text-emerald-600 mr-2" />
                    Pending Uploads
                    <span className="ml-2 text-xs text-gray-500 font-normal">({ingestionProcesses.length})</span>
                  </h2>
                  <div className="space-y-3">
                    {ingestionProcesses.map((ip) => (
                      <div
                        key={ip.id}
                        className="group relative bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md hover:border-emerald-200 transition-all duration-200"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="shrink-0">
                              <FileText className="w-6 h-6 text-gray-400" />
                            </div>
                            <div className="min-w-0">
                              <a
                                href={`/processing/${ip.id}`}
                                onClick={(e) => { if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); navigate(`/processing/${ip.id}`, { state: { from: `/groups/${id}` } }); } }}
                                className={`text-sm font-semibold ${BRAND_TEXT} hover:underline truncate block max-w-[500px] text-left`}
                                title={ip.title || 'Untitled process'}
                              >
                                {ip.title || 'Untitled process'}
                              </a>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a
                              href={`/processing/${ip.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Open pending upload"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Records */}
              <section>
                <h2 className="text-sm font-bold text-gray-900 flex items-center mb-3">
                  <FileText className="w-4 h-4 text-emerald-600 mr-2" />
                  Records
                  <span className="ml-2 text-xs text-gray-500 font-normal">({records.length})</span>
                </h2>
                {records.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No records in this group.</p>
                ) : (
                  <div className="space-y-3">
                    {records.map((record) => (
                      <div
                        key={record.id}
                        className="group relative bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md hover:border-emerald-200 transition-all duration-200"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="shrink-0">{getFileIcon(record.content_type || '', 'w-6 h-6')}</div>
                            <div className="min-w-0">
                              <a
                                href={`/records/${record.id}`}
                                onClick={(e) => { if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); navigate(`/records/${record.id}`); } }}
                                className={`text-sm font-semibold ${BRAND_TEXT} hover:underline truncate block max-w-[500px] text-left`}
                                title={record.title || 'Untitled record'}
                              >
                                {record.title || 'Untitled record'}
                              </a>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a
                              href={`/records/${record.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="View details"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

            </div>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          <p>&copy; 2026 Palmerston North City Council &bull; ARA</p>
        </div>
      </footer>
    </div>
  );
}

// --- Shared Navbar ---

function Navbar({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  return (
    <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <div className="flex items-center space-x-3 group cursor-pointer" onClick={() => navigate('/')}>
            <div className={`w-10 h-10 ${BRAND_BG} rounded-xl shadow-lg shadow-emerald-200 flex items-center justify-center text-white transform group-hover:scale-110 transition-transform duration-300`}>
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <span className={`text-xl font-bold tracking-tight ${BRAND_TEXT}`}>ARA</span>
              <span className="text-xs text-gray-400 block font-medium -mt-1 tracking-widest opacity-60">Automated Records Assistant</span>
            </div>
          </div>
          <AppNavActions />
        </div>
      </div>
    </nav>
  );
}
